import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/i18n/index";
import api from "@/api";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Heart } from "lucide-react";

function PremiumInput({ type = "text", value, onChange, placeholder, showToggle, onToggle, showValue, icon: Icon, ...props }) {
  return (
    <div className="relative group">
      {Icon && (
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
      )}
      <input
        type={showToggle ? (showValue ? "text" : "password") : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full h-14 bg-slate-50/80 border-2 border-slate-200/80 rounded-2xl text-slate-800 placeholder-slate-400 focus:border-primary focus:bg-white focus:shadow-lg focus:shadow-primary/5 outline-none transition-all duration-200 ${Icon ? 'pl-12' : 'px-5'} pr-12`}
        {...props}
      />
      {showToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
        >
          {showValue ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
  );
}

function PremiumButton({ children, loading, variant = "primary", ...props }) {
  return (
    <button
      disabled={loading}
      className={`w-full h-14 rounded-2xl font-semibold text-base flex items-center justify-center gap-2 transition-all duration-200 ${
        variant === "primary"
          ? "bg-gradient-to-r from-primary to-teal-600 text-white shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      } disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none`}
      {...props}
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        children
      )}
    </button>
  );
}

export default function ResetPasswordPage() {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get("token");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // form, success, error, invalid
  
  useEffect(() => {
    if (!token) {
      setStatus("invalid");
    }
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t("resetPassword.passwordsDoNotMatch"));
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error(t("resetPassword.passwordMinLength"));
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        newPassword
      });
      setStatus("success");
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t("error");
      if (errorMessage.includes("expiré") || errorMessage.includes("invalid")) {
        setStatus("error");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const renderInvalid = () => (
    <div className="text-center">
      <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
        <AlertCircle className="w-10 h-10 text-red-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">{t("resetPassword.invalidLink")}</h2>
      <p className="text-slate-500 mb-8">{t("resetPassword.invalidLinkDesc")}</p>
      <PremiumButton onClick={() => navigate("/login")} data-testid="back-to-login-btn">
        {t("resetPassword.backToLogin")}
      </PremiumButton>
    </div>
  );
  
  const renderError = () => (
    <div className="text-center">
      <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-6">
        <AlertCircle className="w-10 h-10 text-amber-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">{t("resetPassword.linkExpired")}</h2>
      <p className="text-slate-500 mb-8">{t("resetPassword.linkExpiredDesc")}</p>
      <PremiumButton onClick={() => navigate("/login")} data-testid="back-to-login-btn">
        {t("resetPassword.requestNewLink")}
      </PremiumButton>
    </div>
  );
  
  const renderSuccess = () => (
    <div className="text-center">
      <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">{t("resetPassword.success")}</h2>
      <p className="text-slate-500 mb-8">{t("resetPassword.successDesc")}</p>
      <PremiumButton onClick={() => navigate("/login")} data-testid="go-to-login-btn">
        {t("resetPassword.goToLogin")}
      </PremiumButton>
    </div>
  );
  
  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">{t("resetPassword.title")}</h2>
        <p className="text-slate-500 mt-2">{t("resetPassword.instructions")}</p>
      </div>
      
      <PremiumInput
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        placeholder={t("resetPassword.newPassword")}
        icon={Lock}
        showToggle
        showValue={showPassword}
        onToggle={() => setShowPassword(!showPassword)}
        required
        data-testid="new-password-input"
      />
      
      <PremiumInput
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder={t("resetPassword.confirmPassword")}
        icon={Lock}
        required
        data-testid="confirm-password-input"
      />
      
      <p className="text-xs text-slate-500">{t("resetPassword.requirements")}</p>
      
      <PremiumButton type="submit" loading={loading} data-testid="reset-password-btn">
        {t("resetPassword.resetButton")}
      </PremiumButton>
    </form>
  );
  
  const renderContent = () => {
    switch (status) {
      case "invalid": return renderInvalid();
      case "error": return renderError();
      case "success": return renderSuccess();
      default: return renderForm();
    }
  };
  
  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-primary to-teal-600 rounded-2xl flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-primary">HANA</h1>
        </div>
        
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {renderContent()}
        </div>
        
        {/* Footer */}
        <p className="text-center text-sm text-slate-400 mt-6">
          © {new Date().getFullYear()} HANA - {t("login.allRightsReserved")}
        </p>
      </div>
    </div>
  );
}
